import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        setSize(data.sizes?.[0] || null);
      })
      .catch(() => navigate("/shop"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <main className="p-20 text-center opacity-60" data-testid="product-loading">Loading...</main>;
  if (!product) return null;

  const handleAdd = () => {
    if (!size) {
      toast.error("Select a size first");
      return;
    }
    addItem(product, size, 1);
    toast.success(`${product.name} added to your bag`);
  };

  return (
    <main className="max-w-7xl mx-auto px-6 sm:px-12 py-16" data-testid="product-detail-page">
      <Link to="/shop" className="text-[11px] uppercase tracking-[0.22em] opacity-60 hover:opacity-100" data-testid="back-to-shop">
        ← The Collection
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 mt-10">
        <div className="overflow-hidden aspect-[4/5] fade-up" style={{ background: "var(--line-soft)" }}>
          <img
            src={product.images[0]}
            alt={product.name}
            className="object-cover w-full h-full"
            data-testid="product-main-image"
          />
        </div>

        <div className="md:py-8 fade-up-delay-1">
          <p className="tracking-overline text-[11px] uppercase opacity-60" data-testid="product-subtitle">
            {product.subtitle}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight" data-testid="product-name">
            {product.name}
          </h1>
          <p className="font-display text-2xl mt-6" data-testid="product-price">
            <span style={{ color: "#0E0E0D" }}>{formatPrice(product.price, product.currency)}</span>
            {product.original_price && product.original_price > product.price && (
              <span
                className="text-base ml-3 line-through"
                style={{ color: "rgba(26,26,25,0.45)" }}
              >
                {formatPrice(product.original_price, product.currency)}
              </span>
            )}
            <span className="text-xs uppercase tracking-[0.2em] opacity-60 ml-3">{product.currency}</span>
          </p>
          {product.original_price && product.original_price > product.price && (
            <p
              className="text-[10px] tracking-[0.28em] uppercase mt-3"
              style={{ color: "rgba(26,26,25,0.6)" }}
              data-testid="product-launch-offer"
            >
              Launch Offer · 20% Off
            </p>
          )}

          <div className="my-10 divider-line" />

          <p className="text-sm leading-relaxed opacity-80" data-testid="product-description">
            {product.description}
          </p>

          <div className="mt-10">
            <p className="tm-label">Select Size</p>
            <div className="flex flex-wrap gap-3 mt-3" data-testid="size-selector">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={"size-pill" + (size === s ? " selected" : "")}
                  data-testid={`size-option-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleAdd}
            className="btn-primary w-full mt-10"
            data-testid="add-to-cart-button"
          >
            Add to Bag — {formatPrice(product.price, product.currency)}
          </button>

          <div className="mt-12 space-y-6 text-sm" style={{ borderTop: "1px solid var(--line)", paddingTop: "24px" }}>
            <div>
              <p className="tm-label">Fabric</p>
              <p className="mt-1 opacity-80">{product.fabric}</p>
            </div>
            <div>
              <p className="tm-label">Care</p>
              <p className="mt-1 opacity-80">{product.care}</p>
            </div>
            <div>
              <p className="tm-label">Atelier Note</p>
              <p className="mt-1 opacity-80 font-display italic text-lg">
                "Each piece is hand-cut. Allow up to seven days before shipping."
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
